<script setup>
import Vue, {ref, inject, computed, provide, reactive, onMounted} from "vue";
import useForm from "@/composables/useForm";
import SubmitterFields from "@/components/trackers/trackerEdit/SubmitterFields.vue";
import trackersFoldersService from "@/services/trackers-folders.service";
import Permissions from "@/components/trackers/trackerEdit/Permissions.vue";
import Categories from "@/components/trackers/trackerEdit/Categories.vue";
import Details from "@/components/trackers/trackerEdit/Details.vue";
import Footer from "@/components/trackers/trackerEdit/Footer.vue";
import projectsService from "@/services/projects.service";
import ProjectStatus from "@/components/projects/ProjectStatus.vue";
import TimeLine from "@/components/trackers/trackerEdit/Timeline.vue";
import {decodeIntervalFromServer} from "@/helpers/date";

const emit = defineEmits(['trackerSaved']);

const projectId = inject('trackerId');
const isNew = inject('isNew');
const folderId = inject('folderId');

const project = reactive({
    name: '',
    folder: null,
    categories: [],
    description: '',
    projectStatusField: null,
    projectStatusValue: {
        options: []
    },
    submitterFields: [],
    permissions: [],
    userPermissions: [],
    startDate: null,
    endDate: null,
});

const {populateForm} = useForm(project);

const submitterFieldsBlock = ref(null);
const loading = ref(false);
const newFilesFieldsCount = ref(0);
const timeline = ref([null, null]);

const canEditInfo = computed(() => !projectId.value || project.userPermissions.includes('tracker-edit'));

onMounted(() => {
    init();
})

async function init() {
    if (projectId.value) {
        const {data} = await projectsService.getProjectEdit(projectId.value);

        populateForm(data);

        if (data.start_date !== null) {
            timeline.value = decodeIntervalFromServer([data.start_date, data.end_date]);
        }
    } else {
        if (folderId.value) {
            const {data} = await trackersFoldersService.getInfo(folderId.value);

            project.permissions = data.permissions;
            project.userPermissions = data.user_permissions;
        } else {
            const {data} = await projectsService.getNewProjectInfo();
            project.userPermissions = data.user_permissions;
        }
    }
}

async function save() {
    if (!submitterFieldsBlock.value.validateFields()) {
        return;
    }

    let form = {
        ...project,
        submitterFields: project.submitterFields.filter(field => submitterFieldsBlock.value.visibleFields.includes(field.field.id)),
    }

    loading.value = true;

    try {
        const {data} = await projectsService.save(projectId.value, form);
        projectId.value = data;

        let newFilesFields = project.submitterFields.filter(field => field.value.files.some(file => file.id === 0));
        newFilesFieldsCount.value = newFilesFields.length;

        if (newFilesFieldsCount.value) {
            newFilesFields.forEach(field => {
                let formData = new FormData();

                field.value.files.forEach(file => {
                    if (file.id === 0) {
                        formData.append('file[]', file);
                    }
                });

                projectsService.saveSubmitterFiles(projectId.value, field.field.id, formData).then(() => {
                    newFilesFieldsCount.value--;

                    if (!newFilesFieldsCount.value) {
                        saveComplete();
                    }
                });
            });
        } else {
            saveComplete();
        }
    } catch (e) {
        console.log(e);
        loading.value = false;
    }
}

function saveComplete() {
    loading.value = false;
    emit('projectSaved');

    Vue.notify({
        group: 'app',
        duration: 5000,
        type: 'success',
        text: !isNew.value ? "Project updated" : "Project created" + '<a class="btn btn-outline-success btn-notify btn-md" href="/projects/' + projectId.value + '" target="_blank">Open Project</a>'
    })
}

provide('tracker', project);
provide('project', project);
provide('isProject', true);
provide('canEditInfo', canEditInfo);
provide('timeline', timeline);
</script>

<template>
    <div>
        <h1 class="modal__title mb-1">Add Project Details</h1>

        <Details/>

        <SubmitterFields ref="submitterFieldsBlock"/>

        <ProjectStatus/>

        <Permissions/>

        <TimeLine/>

        <Categories/>

        <Footer
            :loading="loading"
            @save="save"
        />
    </div>
</template>

<style scoped>

</style>
